import { ChevronDown, Link2, RotateCcw } from "lucide-react";

import { ResponsiveDataTable } from "@/components/data/ResponsiveDataTable";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import {
  formatCaixaBalance,
  formatCaixaQuantity,
  getCaixaKindLabel,
  shouldShowUndoAction,
} from "@/features/caixas/display";
import type { CaixaTransactionRow } from "@/features/caixas/types";

import { CaixasHistorySkeleton } from "./CaixasHistorySkeleton";
import { CaixasMobileTransactionRow } from "./CaixasMobileTransactionRow";
import {
  displayCaixaObservation,
  shortCaixaReference,
} from "./caixaTransactionPresentation";

interface CaixasTransactionLedgerProps {
  rows: CaixaTransactionRow[];
  isLoading?: boolean;
  isFetching?: boolean;
  isFiltered?: boolean;
  error?: unknown;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRetry?: () => void;
  onOpenCarga?: (checkinId: string) => void;
  onUndo: (transaction: CaixaTransactionRow) => void;
}

const dayFormatter = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat("pt-BR", {
  hour: "2-digit",
  minute: "2-digit",
});

function getMovementLabel(transaction: CaixaTransactionRow) {
  if (transaction.kind === "undo") return "Desfazimento";
  if (transaction.undone_by_transaction_id) return "Desfeita";
  return transaction.operation === "CREDIT" ? "Entrada" : "Saída";
}

function hasExpandedDetails(transaction: CaixaTransactionRow) {
  return (
    Boolean(transaction.observation) ||
    Boolean(transaction.undo_of_transaction_id) ||
    Boolean(transaction.undone_by_transaction_id) ||
    shouldShowUndoAction(transaction)
  );
}

function expandedButtonLabel(isExpanded?: boolean) {
  return isExpanded
    ? "Ocultar detalhes da movimentação"
    : "Mostrar detalhes da movimentação";
}

function BalanceComparison({ row }: { row: CaixaTransactionRow }) {
  return (
    <div
      className="caixas-balance-comparison"
      aria-label={`Saldo antes: ${formatCaixaBalance(row.previous_balance)}. Saldo depois: ${formatCaixaBalance(row.new_balance)}.`}
    >
      <span className="caixas-balance-fact">
        <small>Antes</small>
        <strong>{formatCaixaBalance(row.previous_balance)}</strong>
      </span>
      <span className="caixas-balance-fact">
        <small>Depois</small>
        <strong>{formatCaixaBalance(row.new_balance)}</strong>
      </span>
    </div>
  );
}

function ExpandedDetails({
  transaction,
  onUndo,
}: {
  transaction: CaixaTransactionRow;
  onUndo: (transaction: CaixaTransactionRow) => void;
}) {
  return (
    <div className="caixas-expanded-note">
      <div className="caixas-detail-grid">
        <div className="caixas-detail-observation">
          <strong>Observação</strong>
          <p>{displayCaixaObservation(transaction)}</p>
          {transaction.undo_of_transaction_id ? (
            <p>
              Movimentação original{" "}
              {shortCaixaReference(transaction.undo_of_transaction_id)}
            </p>
          ) : null}
          {transaction.undone_by_transaction_id ? (
            <p>
              Desfeita por movimentação{" "}
              {shortCaixaReference(transaction.undone_by_transaction_id)}
            </p>
          ) : null}
        </div>
        <BalanceComparison row={transaction} />
        {shouldShowUndoAction(transaction) ? (
          <button
            type="button"
            className="caixas-table-action caixas-undo-action"
            aria-label="Desfazer movimentação"
            onClick={(event) => {
              event.stopPropagation();
              onUndo(transaction);
            }}
          >
            <RotateCcw size={14} />
            Desfazer
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
}

export function CaixasTransactionLedger({
  rows,
  isLoading = false,
  isFetching = false,
  isFiltered = false,
  error,
  total,
  page,
  pageSize,
  onPageChange,
  onRetry = () => undefined,
  onOpenCarga,
  onUndo,
}: CaixasTransactionLedgerProps) {
  return (
    <OperationalQueryState
      hasData={rows.length > 0}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      isFiltered={isFiltered}
      onRetry={onRetry}
      renderSkeleton={() => <CaixasHistorySkeleton />}
      renderEmpty={({ filtered }) => (
        <section className="caixas-empty-state">
          <div>
            <strong>
              {filtered
                ? "Nenhuma movimentação encontrada"
                : "Nenhuma movimentação de caixas encontrada"}
            </strong>
            <p>
              {filtered
                ? "Ajuste a pesquisa ou limpe os filtros aplicados."
                : "Quando houver entradas ou saídas, elas aparecerão aqui."}
            </p>
          </div>
        </section>
      )}
    >
      <section className="caixas-ledger-shell" aria-busy={isFetching}>
        <div className="caixas-visually-hidden" aria-hidden="true">
          <span>Movimentação</span>
          <span>Quem lançou</span>
          <span>Carga</span>
        </div>
        <ResponsiveDataTable
          className="caixas-ledger-table"
          rows={rows}
          getRowKey={(transaction) => transaction.id}
          desktopHeader={
            <tr>
              <th className="px-4 py-3 text-left">Movimentação</th>
              <th className="px-4 py-3 text-center">Quantidade</th>
              <th className="px-4 py-3 text-center">Data</th>
              <th className="px-4 py-3 text-center">Quem lançou</th>
              <th className="px-4 py-3 text-right">Carga</th>
              <th className="px-3 py-3 text-right" aria-label="Detalhes" />
            </tr>
          }
          renderDesktopRow={(transaction, controls) => {
            const canExpand = hasExpandedDetails(transaction);
            return (
              <tr
                key={transaction.id}
                className={canExpand ? "caixas-observation-row" : ""}
                onClick={() => canExpand && controls?.toggleExpanded()}
              >
                <td className="px-4 py-3 text-sm font-medium">
                  {getMovementLabel(transaction)}
                </td>
                <td className="px-4 py-3 text-center text-sm font-medium">
                  {formatCaixaQuantity(transaction)}
                </td>
                <td className="px-4 py-3 text-center text-sm">
                  <div className="grid justify-items-center gap-0.5">
                    <span>
                      {dayFormatter.format(new Date(transaction.created_at))}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {timeFormatter.format(new Date(transaction.created_at))}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-sm">
                  {transaction.actor_label}
                </td>
                <td className="px-4 py-3 text-right text-sm">
                  <div className="caixas-carga-card">
                    {transaction.checkin_id && onOpenCarga ? (
                      <a
                        href={`/cargas/${transaction.checkin_id}`}
                        className="caixas-table-action"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          onOpenCarga(transaction.checkin_id!);
                        }}
                      >
                        <Link2 size={14} />
                        Abrir carga
                      </a>
                    ) : (
                      <span className="caixas-carga-label">
                        {getCaixaKindLabel(transaction)}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3 text-right text-sm">
                  <div className="caixas-row-detail-cell">
                    {canExpand ? (
                      <button
                        type="button"
                        className="caixas-observation-toggle"
                        data-state={controls?.isExpanded ? "open" : "closed"}
                        aria-label={expandedButtonLabel(controls?.isExpanded)}
                        aria-controls={controls?.expansionId}
                        aria-expanded={controls?.isExpanded}
                        onClick={(event) => {
                          event.stopPropagation();
                          controls?.toggleExpanded();
                        }}
                      >
                        <ChevronDown
                          className="caixas-disclosure-chevron"
                          size={16}
                        />
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          }}
          renderExpandedRow={(transaction, controls) =>
            controls?.mode === "mobile" ? null : (
              <tr id={controls?.expansionId} key={`${transaction.id}-expanded`}>
                <td colSpan={6} className="px-4">
                  <ExpandedDetails transaction={transaction} onUndo={onUndo} />
                </td>
              </tr>
            )
          }
          renderMobileRow={(transaction, controls) => (
            <CaixasMobileTransactionRow
              transaction={transaction}
              isExpanded={Boolean(controls?.isExpanded)}
              onToggle={() => controls?.toggleExpanded()}
              onOpenCarga={onOpenCarga}
              onUndo={onUndo}
            />
          )}
        />
        <OperationalPager
          variant="numbered"
          totalItems={total}
          limit={pageSize}
          offset={page * pageSize}
          page={page}
          pageCount={Math.max(1, Math.ceil(total / pageSize))}
          onPageChange={onPageChange}
          ariaLabel="Paginação do histórico"
          itemLabel="movimentação"
          itemLabelPlural="movimentações"
          className="caixas-pager"
        />
      </section>
    </OperationalQueryState>
  );
}
